import React from 'react';
import { useSearchParams } from 'react-router-dom';

const createRewardCardImage = (rewardAmount) => new Promise((resolve, reject) => {
  const canvas = document.createElement('canvas');
  canvas.width = 1080;
  canvas.height = 1350;

  const context = canvas.getContext('2d');
  if (!context) {
    reject(new Error('Canvas is not supported by this browser.'));
    return;
  }

  const drawRoundedRect = (x, y, width, height, radius, color) => {
    context.beginPath();
    context.roundRect(x, y, width, height, radius);
    context.fillStyle = color;
    context.fill();
  };

  const background = context.createLinearGradient(0, 0, 1080, 1350);
  background.addColorStop(0, '#fef3c7');
  background.addColorStop(0.45, '#f59e0b');
  background.addColorStop(1, '#7c3aed');
  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);

  drawRoundedRect(75, 95, 930, 1160, 48, '#ffffff');

  context.textAlign = 'center';
  context.fillStyle = '#1d4ed8';
  context.font = '700 38px Arial, sans-serif';
  context.fillText('TrustApp Rewards', 540, 240);

  context.fillStyle = '#475569';
  context.font = '500 34px Arial, sans-serif';
  context.fillText('I earned rewards for my activity', 540, 315);

  const rewardPanel = context.createLinearGradient(120, 0, 960, 0);
  rewardPanel.addColorStop(0, '#0f172a');
  rewardPanel.addColorStop(1, '#1d4ed8');
  drawRoundedRect(140, 395, 800, 570, 40, rewardPanel);

  context.fillStyle = '#bfdbfe';
  context.font = '700 34px Arial, sans-serif';
  context.fillText('MY REWARDS', 540, 520);

  context.fillStyle = '#ffffff';
  context.font = '800 128px Arial, sans-serif';
  context.fillText(`₹${new Intl.NumberFormat('en-IN').format(rewardAmount)}`, 540, 690);

  context.fillStyle = '#dbeafe';
  context.font = '500 34px Arial, sans-serif';
  context.fillText('Earned on www.TrustApp.in', 540, 755);

  drawRoundedRect(230, 795, 620, 62, 24, 'rgba(255,255,255,0.16)');
  context.fillStyle = '#ffffff';
  context.font = '700 32px Arial, sans-serif';
  context.fillText('Get local job faster', 540, 837);

  drawRoundedRect(230, 875, 620, 62, 24, 'rgba(255,255,255,0.16)');
  context.fillStyle = '#ffffff';
  context.font = '700 32px Arial, sans-serif';
  context.fillText('Broadcast to earn money', 540, 917);

  context.fillStyle = '#64748b';
  context.font = '500 28px Arial, sans-serif';
  context.fillText('Join TrustApp and start earning rewards.', 540, 1110);

  context.fillStyle = '#2563eb';
  context.font = '700 30px Arial, sans-serif';
  context.fillText('www.TrustApp.in', 540, 1180);

  canvas.toBlob((blob) => {
    if (blob) resolve(blob);
    else reject(new Error('Unable to create the reward card image.'));
  }, 'image/png');
});

const RewardShareCard = () => {
  const [searchParams] = useSearchParams();
  const amountParam = searchParams.get('amount');
  const parsedRewardAmount = Number(amountParam);
  const rewardAmount = Number.isFinite(parsedRewardAmount) && parsedRewardAmount >= 0
    ? parsedRewardAmount
    : 0;
  const [shareMessage, setShareMessage] = React.useState('');

  const downloadImage = (blob) => {
    const imageUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = imageUrl;
    downloadLink.download = `trustapp-reward-${rewardAmount}.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    window.setTimeout(() => URL.revokeObjectURL(imageUrl), 1000);
  };

  const handleDownload = async () => {
    setShareMessage('');
    try {
      const image = await createRewardCardImage(rewardAmount);
      downloadImage(image);
      setShareMessage('Reward card image downloaded. You can post it on Instagram, X, or other apps.');
    } catch (error) {
      console.error('Failed to create reward card image:', error);
      setShareMessage('Unable to create the reward card image. Please try another browser.');
    }
  };

  const handleShare = async () => {
    setShareMessage('');
    try {
      const image = await createRewardCardImage(rewardAmount);
      const file = new File([image], `trustapp-reward-${rewardAmount}.png`, { type: 'image/png' });

      if (
        typeof navigator.share === 'function'
        && typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] })
      ) {
        try {
          await navigator.share({
            title: 'TrustApp Rewards',
            text: `I earned ₹${new Intl.NumberFormat('en-IN').format(rewardAmount)} on TrustApp!`,
            files: [file],
          });
          return;
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.warn('Native image sharing failed; downloading the reward card instead.', error);
        }
      }

      downloadImage(image);
      setShareMessage('Image sharing is not supported here, so the reward card was downloaded. You can post it from your photos.');
    } catch (error) {
      console.error('Failed to share reward card image:', error);
      setShareMessage('Unable to share the image. Try downloading it instead.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #fef3c7 0%, #f59e0b 35%, #7c3aed 100%)',
      padding: '24px',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '420px',
        background: 'rgba(255,255,255,0.96)',
        borderRadius: '24px',
        padding: '28px 22px',
        boxShadow: '0 18px 45px rgba(0,0,0,0.18)',
        border: '1px solid rgba(255,255,255,0.7)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          <div style={{
            display: 'inline-flex',
            width: '68px',
            height: '68px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #fef3c7, #f59e0b)',
            color: '#78350f',
            fontSize: '2.2rem',
            fontWeight: '800',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 18px rgba(245, 158, 11, 0.25)',
          }}>₹</div>
          <p style={{ margin: '14px 0 8px', fontSize: '0.8rem', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#64748b', fontWeight: '700' }}>
            TrustApp Rewards
          </p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)',
          borderRadius: '22px',
          padding: '26px 18px',
          color: '#fff',
          textAlign: 'center',
          marginBottom: '18px',
          boxShadow: 'inset 0 0 20px rgba(255,255,255,0.08)',
        }}>
          <div style={{ fontSize: '0.8rem', opacity: 0.9, letterSpacing: '1px', textTransform: 'uppercase' }}>
            I earned
          </div>
          <div style={{ fontSize: '3rem', fontWeight: '900', lineHeight: 1.1, margin: '10px 0 0' }}>
            ₹{rewardAmount}
          </div>
          <div style={{ fontSize: '0.92rem', marginTop: '10px', opacity: 0.95 }}>
            Earned on www.TrustApp.in
          </div>
          <div style={{
            marginTop: '18px',
            display: 'grid',
            gap: '8px',
            fontSize: '0.9rem',
            fontWeight: '700',
          }}>
            <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(255,255,255,0.16)' }}>
              Get local job faster
            </div>
            <div style={{ padding: '8px', borderRadius: '10px', background: 'rgba(255,255,255,0.16)' }}>
              Broadcast to earn money
            </div>
          </div>
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: '0.92rem',
          lineHeight: 1.6,
          color: '#334155',
          margin: '0 0 20px',
        }}>
          Download the image to post it on Instagram or X, or share it directly from a supported device.
        </p>

        <button
          onClick={handleDownload}
          style={{
            width: '100%',
            border: 'none',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
            color: '#fff',
            fontSize: '1rem',
            fontWeight: '700',
            padding: '14px 18px',
            cursor: 'pointer',
            boxShadow: '0 10px 20px rgba(37, 99, 235, 0.25)',
          }}
        >
          Download reward image
        </button>
        <button
          onClick={handleShare}
          style={{
            width: '100%',
            marginTop: '10px',
            border: '1px solid #2563eb',
            borderRadius: '12px',
            background: '#ffffff',
            color: '#1d4ed8',
            fontSize: '1rem',
            fontWeight: '700',
            padding: '13px 18px',
            cursor: 'pointer',
          }}
        >
          Share image
        </button>
        {shareMessage && (
          <p role="status" style={{ margin: '12px 0 0', textAlign: 'center', fontSize: '0.82rem', color: '#475569' }}>
            {shareMessage}
          </p>
        )}

      </div>
    </div>
  );
};

export default RewardShareCard;
